import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Volume2, Pause } from 'lucide-react';

const btnBase = (dense) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  marginTop: dense ? 6 : 12,
  padding: dense ? '8px 14px' : '10px 16px',
  minHeight: dense ? 40 : 44,
  borderRadius: 14,
  border: '2px solid var(--color-primary)',
  background: 'var(--color-surface, var(--color-bg-card))',
  color: 'var(--color-primary)',
  fontWeight: 700,
  fontSize: dense ? 14 : 15,
  cursor: 'pointer',
  boxSizing: 'border-box',
});

const focusVisible = {
  outline: '3px solid var(--color-primary)',
  outlineOffset: 2,
};

const HAVE_FUTURE_DATA = 3;

/**
 * Play/pause control for one static narration file per lesson step.
 * Parent must pause/reset the underlying audio when the step changes.
 *
 * @param {object} props
 * @param {string} props.src - Public URL (e.g. /audio/.../file.mp3)
 * @param {import('react').MutableRefObject<HTMLAudioElement | null>} props.audioRef
 * @param {string} [props.stepId]
 * @param {string} [props.stepTitle]
 * @param {boolean} [props.autoplay] - If true, play after load (may be blocked by browser until user gesture)
 * @param {boolean} [props.dense] – tighter top margin (e.g. narration below activity on small screens)
 * @param {'full'|'icon'} [props.variant] – `icon`: compact control for lesson bottom bar (48px target)
 */
export default function LessonStepNarration({
  src,
  audioRef,
  stepId,
  stepTitle,
  autoplay = false,
  dense = false,
  variant = 'full',
}) {
  const [phase, setPhase] = useState('idle');
  const [unavailable, setUnavailable] = useState(false);
  const syncFromElement = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    if (el.ended) {
      setPhase('replay');
      return;
    }
    if (!el.paused) {
      setPhase('playing');
      return;
    }
    if (el.currentTime > 0) {
      setPhase('replay');
      return;
    }
    setPhase('idle');
  }, [audioRef]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onPlay = () => setPhase('playing');
    const onPause = () => {
      if (el.ended) setPhase('replay');
      else if (el.currentTime > 0) setPhase('replay');
      else setPhase('idle');
    };
    const onEnded = () => setPhase('replay');
    const onError = () => {
      setUnavailable(true);
      setPhase('idle');
    };
    el.addEventListener('play', onPlay);
    el.addEventListener('pause', onPause);
    el.addEventListener('ended', onEnded);
    el.addEventListener('error', onError);
    return () => {
      el.removeEventListener('play', onPlay);
      el.removeEventListener('pause', onPause);
      el.removeEventListener('ended', onEnded);
      el.removeEventListener('error', onError);
    };
  }, [audioRef, src, stepId]);

  useEffect(() => {
    setUnavailable(false);
    setPhase('idle');
    const el = audioRef.current;
    if (!el || !src) return;
    el.pause();
    el.currentTime = 0;
    el.src = src;
    el.load();

    let cancelled = false;

    const runAutoplay = () => {
      if (cancelled || !autoplay) return;
      void el.play().catch(() => {
        if (!cancelled) syncFromElement();
      });
    };

    const onCanPlay = () => {
      runAutoplay();
    };

    if (autoplay) {
      if (el.readyState >= HAVE_FUTURE_DATA) {
        runAutoplay();
      } else {
        el.addEventListener('canplay', onCanPlay, { once: true });
      }
    }

    syncFromElement();

    return () => {
      cancelled = true;
      el.removeEventListener('canplay', onCanPlay);
    };
  }, [src, stepId, audioRef, syncFromElement, autoplay]);

  const labelContext = stepTitle ? `${stepTitle}` : 'this step';

  let buttonLabel = `Listen to narration for ${labelContext}`;
  let visibleText = '🔊 Listen';
  if (phase === 'playing') {
    buttonLabel = `Pause narration for ${labelContext}`;
    visibleText = '🔊 Playing...';
  } else if (phase === 'replay') {
    buttonLabel = `Replay narration for ${labelContext}`;
    visibleText = '🔊 Replay';
  }

  const handleClick = () => {
    const el = audioRef.current;
    if (!el || unavailable) return;
    if (!el.paused) {
      el.pause();
      return;
    }
    void el.play().catch(() => {
      setUnavailable(true);
    });
  };

  if (unavailable) {
    if (variant === 'icon') {
      return (
        <button
          type="button"
          disabled
          aria-label="Narration unavailable"
          title="Audio unavailable"
          style={{
            flex: '0 0 48px',
            width: 48,
            height: 48,
            borderRadius: 12,
            border: '1px dashed var(--color-border)',
            background: 'var(--color-bg-card)',
            opacity: 0.45,
            cursor: 'not-allowed',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Volume2 size={22} strokeWidth={2} aria-hidden />
        </button>
      );
    }
    return (
      <p
        role="status"
        style={{
          margin: '12px 0 0',
          fontSize: 14,
          lineHeight: 1.5,
          opacity: 0.85,
          padding: '10px 14px',
          borderRadius: 14,
          background: 'var(--color-bg)',
          border: '1px solid var(--color-border)',
        }}
      >
        Audio is not available yet — you can keep reading and continue the lesson.
      </p>
    );
  }

  if (variant === 'icon') {
    return (
      <button
        type="button"
        onClick={handleClick}
        aria-label={buttonLabel}
        title={visibleText}
        style={{
          flex: '0 0 48px',
          width: 48,
          height: 48,
          minWidth: 48,
          minHeight: 48,
          borderRadius: 12,
          border: '2px solid var(--color-primary)',
          background: 'var(--color-surface, var(--color-bg-card))',
          color: 'var(--color-primary)',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxSizing: 'border-box',
          boxShadow: '0 1px 6px var(--color-shadow)',
        }}
        onFocus={(e) => {
          Object.assign(e.currentTarget.style, focusVisible);
        }}
        onBlur={(e) => {
          e.currentTarget.style.outline = '';
          e.currentTarget.style.outlineOffset = '';
        }}
      >
        {phase === 'playing' ? <Pause size={22} strokeWidth={2.2} aria-hidden /> : <Volume2 size={22} strokeWidth={2.2} aria-hidden />}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={buttonLabel}
      style={btnBase(dense)}
      onFocus={(e) => {
        Object.assign(e.currentTarget.style, focusVisible);
      }}
      onBlur={(e) => {
        e.currentTarget.style.outline = '';
        e.currentTarget.style.outlineOffset = '';
      }}
    >
      {visibleText}
    </button>
  );
}
